/**
 * useApplicationHistory
 *
 * Read-time aggregation of the unified Notes/History thread for an
 * Application rollup. The Application is "really all one asset", so the
 * History tab and the Notes tab (both derive from `history[]` — see
 * `getNotes` in `@/sections/Notes`) must read as one merged thread whether
 * the user opened the parent Application or any member observation.
 *
 * From the parent `:Application` keyed by the observation's `group_key`
 * (resolved by `applicationRollupKey`), walk outgoing `HAS_MEMBER` to every
 * member. We do NOT walk `HAS_VULNERABILITY` here — only the per-node
 * `history` property is needed.
 *
 * The merged result is the union of the parent Application's `history` and
 * each member's `history`, sorted newest-first and de-duplicated (the
 * Application also carries the `:Asset` label, so the parent can surface as
 * its own member-adjacent node — guard against double-counting identical
 * records).
 *
 * @see Notes.tsx getNotes (notes derive from history[].comment)
 */
import { useMemo } from 'react';

import { useMy } from '@/hooks/useMy';
import { applicationRollupKey } from '@/sections/asset/utils';
import type { Asset, EntityHistory, TreeNode } from '@/types';
import { QueryStatus } from '@/utils/api';
import { flattenAssetGraphResponse } from '@/utils/api/flattenAssetGraphResponse';

export interface UseApplicationHistoryResult {
  /** Merged parent + member history, newest-first, de-duplicated. */
  history: EntityHistory[];
  /** Underlying query status (pending while the tree query is in-flight). */
  status: QueryStatus;
}

/**
 * Stable signature for an EntityHistory record, used to de-dupe the union.
 * `JSON.stringify` of the field tuple avoids separator-collision (a field
 * value can't be confused with the delimiter). Includes `by` (author) so
 * two members with coincidentally-identical status transitions by different
 * actors are NOT collapsed — dedup only removes truly-identical records
 * (e.g. the parent surfacing via its `:Asset` label).
 */
function historySignature(entry: EntityHistory): string {
  return JSON.stringify([
    entry.updated ?? '',
    entry.comment ?? '',
    entry.from ?? '',
    entry.to ?? '',
    entry.by ?? '',
  ]);
}

/** Parse a sortable epoch from an EntityHistory entry, 0 when absent/invalid. */
function historyTimestamp(entry: EntityHistory): number {
  const t = entry.updated ? new Date(entry.updated).getTime() : NaN;
  return Number.isNaN(t) ? 0 : t;
}

/**
 * Aggregate the unified history thread for the Application that `asset`
 * rolls up under. Keyed on `applicationRollupKey(asset)` — the asset's own
 * key when it IS the :Application (the resolve-up path, where the re-fetched
 * parent carries no `group_key`) or the parent's `group_key` when a member
 * is opened directly. Disabled (returns empty) when neither applies.
 *
 * NOTE: assumes the parent group is an `:Application`. Today `group_key` is
 * always `#application#…` (Application is the only concrete `model.Group`),
 * so the `labels: ['Application']` read and the write-to-`group_key` path
 * agree. If a non-Application group type ships, both sites must be revisited.
 */
export function useApplicationHistory(
  asset: Asset
): UseApplicationHistoryResult {
  const appKey = applicationRollupKey(asset);

  // HAS_MEMBER walk (same rollup pattern as applicationRollupKey), minus HAS_VULNERABILITY.
  const { data, status } = useMy(
    {
      resource: 'asset',
      tree: true,
      query: {
        node: {
          labels: ['Application'],
          filters: [{ field: 'key', operator: '=', value: appKey || '' }],
          relationships: [
            {
              label: 'HAS_MEMBER',
              target: { labels: ['Asset'] },
            },
          ],
        },
      },
    },
    { enabled: Boolean(appKey) }
  );

  const history = useMemo<EntityHistory[]>(() => {
    if (!data?.length) return [];

    // The first TreeNode IS the parent :Application (the primary node of
    // the query). Its relationships[] are the HAS_MEMBER edges.
    const root = data[0] as TreeNode | undefined;
    const rootResult = root?.node?.result;

    const collected: EntityHistory[] = [];

    // Parent Application history (from the `applications` bucket of the
    // root node — the Application is not an asset subtype flattened by
    // flattenAssetGraphResponse, so read it directly).
    const apps =
      (rootResult as unknown as { applications?: Asset[] } | undefined)
        ?.applications ?? [];
    const parentApp = apps[0];
    if (parentApp?.history?.length) {
      collected.push(...parentApp.history);
    }

    // Member history — members come back bucketed by asset subtype; reuse
    // flattenAssetGraphResponse rather than re-listing the buckets.
    const memberRels = root?.node?.relationships ?? [];
    for (const memberRel of memberRels) {
      const members = flattenAssetGraphResponse(memberRel.target?.result);
      for (const member of members) {
        if (member?.history?.length) {
          collected.push(...member.history);
        }
      }
    }

    // De-dupe identical records (the Application carries `:Asset`, so the
    // parent can appear as its own member-adjacent node) then sort
    // newest-first.
    const seen = new Set<string>();
    const deduped: EntityHistory[] = [];
    for (const entry of collected) {
      const sig = historySignature(entry);
      if (seen.has(sig)) continue;
      seen.add(sig);
      deduped.push(entry);
    }

    deduped.sort((a, b) => historyTimestamp(b) - historyTimestamp(a));
    return deduped;
  }, [data]);

  return { history, status };
}
